import ChatApiService from "@api-services/chat/ChatApiService";
import { SharedBetSlipsApiService } from "@api-services/shared-bet-slips";
import {
	ChannelIconsDto,
	ChannelResourceTypeDto,
	ChannelResponseMessage,
} from "@api-types/chat";
import {
	ChannelInfoDataFromList,
	ChannelInfoDataFromOffer,
	ChannelInfoDataFromSession,
	ChatEntryPoint,
	ChatWindowState,
	ReplyMessageType,
} from "@data-types/chat";
import { ChatSDKService } from "@services/chat";
import { IErrorMessage } from "@state/providers/diagnosticsProvider";
import { localizationService } from "@state/services";
import {
	getAgencyId,
	getCurrentCulture,
	getCurrentLanguage,
	getIs24hoursPassed,
	getIsDateToday,
} from "@utils";
import { SocialMediaShareTypeEnum } from "@data-types/analytics-tracker/SocialMediaShareTypeEnum";
import { analyticsTracker } from "@utils/analytics";
import { DateTime } from "luxon";
import { action, observable, runInAction } from "mobx";

import { LoaderStore } from "../common";
import { ChatApiError } from "./ChatApiErrorModel";
import type { ChatViewStore } from "./ChatViewStore";
import orderBy from "lodash/orderBy";

export class ChatChannelHandler {
	chatViewStore: ChatViewStore;
	messageLoader: LoaderStore;
	flaggingLoader: LoaderStore;
	channelInfoLoader: LoaderStore;
	messageListLoader: LoaderStore;

	disposerList: Amity.Unsubscriber[] = [];
	subscribedChannelId: string | null = null;
	subscribedActiveSubChannelId: string | null = null;

	public handleVisibilityChange: () => Promise<void>;

	@observable activeChannelInfo: Amity.Channel | null;
	@observable messagesPaginationObject: Amity.LiveCollection<
		Amity.Message<any>
	> | null = null;
	@observable messageList: AmityMessageMutated[] = [];
	@observable iconProps: ChannelIconsDto | undefined;

	@observable supportChatDisposer: Amity.Unsubscriber;
	@observable supportMessagePaginationObject: Amity.LiveCollection<
		Amity.Message<any>
	> | null = null;
	@observable supportChannelMessageList: GroupedMessagesWithTotal | null;
	@observable supportChannelId: string | null = null;
	@observable activeSupportChannelInfo: Amity.Channel | null;

	@observable firstVisit: boolean;

	constructor(chatViewStore: ChatViewStore) {
		this.chatViewStore = chatViewStore;
		this.messageLoader = new LoaderStore();
		this.flaggingLoader = new LoaderStore();
		this.channelInfoLoader = new LoaderStore();
		this.messageListLoader = new LoaderStore();
	}

	addResubscribeListener() {
		this.handleVisibilityChange = async () => {
			if (document.visibilityState === "visible") {
				await this.resubscribeToChatMessages();
			}
		};

		document.addEventListener(
			"visibilitychange",
			this.handleVisibilityChange
		);
	}

	@action.bound
	async resubscribeToChatMessages() {
		if (!this.supportChannelId) {
			return;
		}

		await this.joinSupportChannel();
	}

	@action.bound
	async joinSupportChannel() {
		const username = this.chatViewStore.username;

		const currentActiveClient = ChatSDKService.returnCurrentClient();

		if (!currentActiveClient?.token?.accessToken || !username) {
			throw new ChatApiError(true);
		}

		try {
			const { channels, message } =
				await ChatApiService.getSupportChannel(
					username,
					currentActiveClient?.token?.accessToken
				);

			runInAction(() => {
				if (channels !== null) {
					this.supportChannelId = channels[0].channelId;
				}

				if (message == ChannelResponseMessage.SUPPORT_CH_NOT_EXIST) {
					this.firstVisit = true;
				}

				if (message == ChannelResponseMessage.USER_NOT_FOUND) {
					throw new Error(ChannelResponseMessage.USER_NOT_FOUND);
				}
			});
		} catch (error) {
			console.error("Chat ERR: Failed retrieving support channel", error);
			throw new ChatApiError(
				true,
				"CHAT.SUPPORT.FAILED_JOIN_SUPPORT_CHANNEL"
			);
		} finally {
			this.chatViewStore.chatLoader.resume();
		}

		const channelId = this.supportChannelId;

		if (!channelId) {
			return;
		}

		const channelDisposer = ChatSDKService.subscribeToNewMessages(
			channelId,
			(e) => {
				{
					this.channelMessagesCallback(e, channelId, true);
				}
			}
		);

		this.supportChatDisposer = channelDisposer;
		this.markChannelAsRead();

		this.chatViewStore.chatLoader.resume();
	}

	@action.bound
	async markChannelAsRead() {
		if (
			!this.supportChannelId ||
			!this.chatViewStore.unreadSupportMessages
		) {
			return null;
		}

		this.chatViewStore.unreadSupportMessages = false;

		try {
			await ChatSDKService.markAsRead(this.supportChannelId);
		} catch (error) {
			throw new ChatApiError(true);
		}
	}

	async joinUserIntoChannelPerType(
		entryPoint: ChatEntryPoint,
		channelData:
			| ChannelInfoDataFromOffer
			| ChannelInfoDataFromList
			| ChannelInfoDataFromSession
			| null
	) {
		if (
			entryPoint === ChatEntryPoint.BET_SLIP_SHARE ||
			entryPoint === ChatEntryPoint.USER_MENU ||
			!channelData
		) {
			const channelSessionData =
				this.chatViewStore.channelSessionHandler.getChannelInfoFromSession();

			if (channelSessionData) {
				await this.joinChannelFromSession(channelSessionData.channelId);
				return;
			}

			const { displayName, channelId } =
				this.chatViewStore.chatListHandler.channelList[0];

			await this.joinChannelFromList(channelId, displayName);
			return;
		}

		if (
			entryPoint === ChatEntryPoint.LIST &&
			"channelId" in channelData &&
			"displayName" in channelData
		) {
			//used for joining into channels from header list
			await this.joinChannelFromList(
				channelData.channelId,
				channelData.displayName,
				channelData.sportAbrv || "",
				channelData.categoryAbrv || "",
				channelData.tournamentAbrv || ""
			);
			return;
		}

		if (
			entryPoint === ChatEntryPoint.OFFER &&
			"channelType" in channelData &&
			"resourceId" in channelData
		) {
			if (
				!this.chatViewStore.chatWidgetVisible ||
				!this.chatViewStore.chatBoxVisible
			) {
				this.chatViewStore.toggleChatWidget(true);
				this.chatViewStore.toggleChatBox(true);
			}
			this.chatViewStore.toggleChatList(false);

			const channels = this.chatViewStore.chatListHandler.channelList;

			const validChannel = channels.find(
				(channel) => channel.sportAbrv === channelData.sportAbrv
			);

			if (!validChannel) {
				const globalChannel = channels.find((channel) =>
					channel.channelId.includes("global")
				);
				if (globalChannel) {
					await this.joinChannelFromSession(globalChannel?.channelId);
					return;
				}
			}

			await this.joinUserIntoChannel(
				channelData.channelType,
				channelData.resourceId
			);
			return;
		}

		if (
			entryPoint === ChatEntryPoint.SESSION &&
			"channelId" in channelData &&
			"chatState" in channelData
		) {
			await this.joinChannelFromSession(channelData.channelId);
			return;
		}
	}

	async joinChannelFromSession(channelId: string) {
		const channelInfo = await this.queryChannelInfo(channelId);

		this.updateIconProps(channelInfo);

		const joinStatus = await ChatSDKService.joinChannel(
			channelInfo.channelId
		);

		if (!joinStatus) {
			console.error("Chat ERR: Failed joining user into channel");
			throw new ChatApiError(
				true,
				"CHAT.ERR_HANDLING.FAILED.JOIN_INTO_CHANNEL"
			);
		}

		this.handleChannelListeners(
			channelId,
			channelInfo.displayName,
			channelInfo.sportAbrv,
			channelInfo.categoryAbrv,
			channelInfo.tournamentAbrv
		);
	}

	async joinChannelFromList(
		channelId: string,
		displayName: string,
		sportAbrv?: string,
		categoryAbrv?: string,
		tournamentAbrv?: string
	) {
		this.updateIconProps({
			sportAbrv,
			categoryAbrv,
			tournamentAbrv,
		});

		const joinStatus = await ChatSDKService.joinChannel(channelId);

		if (!joinStatus) {
			console.error("Chat ERR: Failed joining user into channel");
			throw new ChatApiError(
				true,
				"CHAT.ERR_HANDLING.FAILED.JOIN_INTO_CHANNEL"
			);
		}

		this.handleChannelListeners(
			channelId,
			displayName,
			sportAbrv,
			categoryAbrv,
			tournamentAbrv
		);
	}

	async joinUserIntoChannel(
		channelType: ChannelResourceTypeDto,
		resourceId: string
	) {
		const channelData = await this.fetchChannelData(
			channelType,
			resourceId
		);

		const queryChannel = channelData.channels?.[0];

		this.updateIconProps(queryChannel);

		const joinStatus = await ChatSDKService.joinChannel(
			queryChannel.channelId
		);

		if (!joinStatus) {
			console.error("Chat ERR: Failed joining user into channel");
			throw new ChatApiError(
				true,
				"CHAT.ERR_HANDLING.FAILED.JOIN_INTO_CHANNEL"
			);
		}

		this.handleChannelListeners(
			queryChannel.channelId,
			queryChannel.displayName,
			queryChannel.sportAbrv,
			queryChannel.categoryAbrv,
			queryChannel.tournamentAbrv
		);
	}

	async queryChannelInfo(channelId: string) {
		try {
			return await ChatApiService.queryChannelInfo(
				channelId,
				getCurrentLanguage()
			);
		} catch (error) {
			throw new ChatApiError(
				true,
				"CHAT.ERR_HANDLING.FAILED_QUERYING_CHANNEL_INFO"
			);
		}
	}

	async fetchChannelData(
		channelType: ChannelResourceTypeDto,
		resourceId: string
	) {
		try {
			return await ChatApiService.fetchChannelData({
				channelResourceType: channelType,
				resourceId: resourceId,
				agencyId: getAgencyId(),
				culture: getCurrentLanguage(),
			});
		} catch (error) {
			throw new ChatApiError(
				true,
				"CHAT.ERR_HANDLING.FAILED_FETCHING_CHANNEL_DATA"
			);
		}
	}

	async replyToMessage(replyMessage: ReplyMessageType) {
		if (!this.subscribedActiveSubChannelId) {
			return;
		}

		const replyingBetSlip = this.chatViewStore.sharedBetSlipData;

		if (replyingBetSlip) {
			await ChatSDKService.replyToMessage(
				this.subscribedActiveSubChannelId,
				replyMessage,
				replyingBetSlip
			);
			if (replyingBetSlip.id) {
				analyticsTracker.trackSocialMediaShared(
					SocialMediaShareTypeEnum.ChatShare,
					replyingBetSlip.id
				);
			}
		} else {
			await ChatSDKService.replyToMessage(
				this.subscribedActiveSubChannelId,
				replyMessage
			);
		}

		this.chatViewStore.setSelectedMessageToReplyOn(null);
		this.chatViewStore.setSharedBetSlipData(null);
	}

	async sendMessage(text: string, isLink?: boolean, isMultiOffer?: boolean) {
		if (!this.subscribedActiveSubChannelId) {
			return;
		}

		const sharedBetSlip = this.chatViewStore.sharedBetSlipData;
		let isBetSlipSent = false;

		try {
			this.messageLoader.suspend();
			if (sharedBetSlip) {
				await ChatSDKService.createCustomMessage(
					this.subscribedActiveSubChannelId,
					sharedBetSlip,
					text,
					isLink,
					isMultiOffer
				);
				isBetSlipSent = true;
			} else {
				await ChatSDKService.createMessage({
					subChannelId: this.subscribedActiveSubChannelId,
					message: text,
					isLink,
				});
			}

			this.chatViewStore.setSharedBetSlipData(null);
		} catch (error) {
			console.error("Chat ERR: Failed sending message into channel");
			this.chatViewStore.handleError(error);
		} finally {
			this.messageLoader.resume();
		}

		if (sharedBetSlip?.id) {
			try {
				await SharedBetSlipsApiService.insertSharedBetSlip(
					sharedBetSlip.id,
					1
				);
				if (isBetSlipSent) {
					analyticsTracker.trackSocialMediaShared(
						SocialMediaShareTypeEnum.ChatShare,
						sharedBetSlip.id
					);
				}
			} catch (error) {
				console.error("Failed to insert shared bet slip");
			}
		}
	}

	async createSupportChannel() {
		let result = false;

		try {
			const accessToken =
				this.chatViewStore.chatClientHandler.currentClient?.token
					?.accessToken;
			const username = this.chatViewStore.username;

			if (username && accessToken) {
				const { channels } = await ChatApiService.createSupportChannel(
					username,
					accessToken
				);

				runInAction(() => {
					if (channels) {
						this.firstVisit = false;
						this.supportChannelId = channels[0].channelId;

						result = true;
					} else {
						throw Error();
					}
				});
			}
		} catch (error) {
			console.error("Chat ERR: Failed creating support channel.");
			this.chatViewStore.rootStore.notificationStore.error(
				localizationService.t(
					"CHAT.SUPPORT.FAILED_JOIN_SUPPORT_CHANNEL"
				)
			);
		}

		return result;
	}

	async sendSupportMsg(
		text: string,
		file?: File,
		diagnosticsData?: IErrorMessage | null
	) {
		let isChannelCreated = false;

		// if user's first visit to support chat, create the channel
		if (this.firstVisit || this.firstVisit == undefined) {
			isChannelCreated = await this.createSupportChannel();
		}

		if (!this.supportChannelId) {
			return;
		}

		try {
			await ChatSDKService.createMessage({
				subChannelId: this.supportChannelId,
				message: text,
				file,
				diagnosticsData,
			});

			// trigger only if support channel is created for the first time
			if (isChannelCreated) {
				const channelDisposer = ChatSDKService.subscribeToNewMessages(
					this.supportChannelId,
					(e) => {
						{
							this.supportChannelId &&
								this.channelMessagesCallback(
									e,
									this.supportChannelId,
									true
								);
						}
					}
				);

				this.supportChatDisposer = channelDisposer;
			}
		} catch (error) {
			console.error("Chat ERR: Failed sending message into channel");
			this.chatViewStore.handleError(error);
		}
	}

	handleChannelListeners(
		channelId: string,
		channelDisplayName: string,
		sportAbrv: string | undefined,
		categoryAbrv: string | undefined,
		tournamentAbrv: string | undefined
	) {
		this.createChannelInfoListener(
			channelId,
			channelDisplayName,
			sportAbrv,
			categoryAbrv,
			tournamentAbrv
		);
	}

	createChannelInfoListener(
		channelId: string,
		channelDisplayName: string,
		sportAbrv: string | undefined,
		categoryAbrv: string | undefined,
		tournamentAbrv: string | undefined
	) {
		this.channelInfoLoader.suspend();
		try {
			const channelInfoDisposer = ChatSDKService.getChannelInfo(
				channelId,
				(e) =>
					this.channelInfoCallback(
						e,
						channelDisplayName,
						sportAbrv,
						categoryAbrv,
						tournamentAbrv
					)
			);

			this.updateDisposerList(channelInfoDisposer);
		} catch (error) {
			console.error("Chat ERR: Failed querying channel info");
			this.channelInfoLoader.resume();
			throw error;
		}
	}

	channelInfoCallback = (
		channelInfo: Amity.LiveObject<Amity.Channel>,
		channelDisplayName: string,
		sportAbrv: string | undefined,
		categoryAbrv: string | undefined,
		tournamentAbrv: string | undefined
	) => {
		if (channelInfo.error) {
			//update error object
			console.error(
				"Chat ERR: channel info callback error",
				channelInfo.error
			);
			this.channelInfoLoader.resume();
			this.chatViewStore.handleError(channelInfo.error);
			return;
		}

		if (channelInfo.loading) {
			this.channelInfoLoader.suspend();
			return;
		}

		if (channelInfo.data.isDeleted) {
			return;
		}

		const channelData = {
			...channelInfo.data,
			displayName: channelDisplayName,
			sportAbrv: sportAbrv,
			categoryAbrv: categoryAbrv,
			tournamentAbrv: tournamentAbrv,
		};

		this.setActiveChannel(channelData);

		//update session once successfully joined channel
		this.chatViewStore.channelSessionHandler.setChannelInfoIntoSession(
			channelData.channelId,
			this.chatViewStore.chatBoxVisible
				? ChatWindowState.OPENED
				: ChatWindowState.MINIMIZED
		);

		this.createChannelMessageListener(
			channelData.channelId,
			channelInfo.data.defaultSubChannelId
		);

		this.subscribedActiveSubChannelId =
			channelInfo.data.defaultSubChannelId;

		this.channelInfoLoader.resume();
	};

	createChannelMessageListener(channelId: string, subChannelId: string) {
		if (channelId === this.subscribedChannelId) {
			//already created message listeners for this channel
			return;
		}

		try {
			this.messageListLoader.suspend();

			//resume in case main loader still loading
			this.chatViewStore.chatLoader.resume();

			const channelMessageListenerDisposer =
				ChatSDKService.subscribeToNewMessages(subChannelId, (e) =>
					this.channelMessagesCallback(e, channelId)
				);

			this.updateDisposerList(channelMessageListenerDisposer);
		} catch (error) {
			console.error("Chat ERR: channel info callback error");
			this.messageListLoader.resume();
			throw error;
		}
	}

	channelMessagesCallback = (
		messageInfo: Amity.LiveCollection<Amity.Message<any>>,
		channelId: string,
		isSupport?: boolean
	) => {
		if (messageInfo.error) {
			console.error("Chat ERR: channel messages callback error");
			this.messageListLoader.resume();
			this.chatViewStore.handleError(messageInfo.error);
		}

		if (messageInfo.loading) {
			return;
		}

		if (!isSupport) {
			this.subscribedChannelId = channelId;
		}

		this.setMessagePaginationObject(messageInfo, isSupport);

		this.sortChannelMessages(messageInfo.data, isSupport);

		this.messageListLoader.resume();
	};

	sortChannelMessages(messages: Amity.Message<any>[], isSupport?: boolean) {
		const updatedMessages = this.updateMessageCreatorId(
			messages,
			isSupport
		);
		const orderedMessages = orderBy(updatedMessages, "createdAt", "asc");

		if (isSupport) {
			const groupedByDate = this.groupMessagesByDate(
				orderBy(updatedMessages, "createdAt", "asc")
			);
			this.updateSupportMessageList(groupedByDate);
		} else {
			this.updateMessageList(orderedMessages);
		}
	}

	groupMessagesByDate(
		messages: AmityMessageMutated[]
	): GroupedMessagesWithTotal {
		let showAdditionalInfo = false;
		const lastMessage = messages[messages.length - 1];

		if (getIs24hoursPassed(lastMessage?.createdAt)) {
			showAdditionalInfo = true;
		}

		const groupedMessages = messages
			.reverse()
			.reduce((acc: GroupedMessages, message) => {
				const date = getIsDateToday(message.createdAt)
					? localizationService.t("OFFER.TIME.TODAY")
					: DateTime.fromISO(message.createdAt).toLocaleString(
							DateTime.DATE_MED,
							{
								locale: getCurrentCulture(),
							}
						);
				if (!acc[date]) {
					acc[date] = [];
				}
				acc[date].push(message);
				return acc;
			}, {});

		return {
			groupedMessages,
			totalMessages: messages.length,
			showAdditionalInfo,
		};
	}

	updateMessageCreatorId(
		messages: Amity.Message<any>[],
		isSupport?: boolean
	): AmityMessageMutated[] {
		//SGUID-CHAT_NAME to CHAT_NAME
		//infinite scrolling plugin does array.concat, joining already modified messages and new messages
		//because of this needed to add a check if message is already mutated
		return messages.map((msg: AmityMessageMutated) => {
			if (msg?.creatorIdMutated) {
				return msg;
			}

			if (msg.creatorId.includes("_")) {
				msg.creatorId = msg.creatorId.split("_")[1];
			} else if (
				isSupport &&
				(msg.creatorId == "Admin" || msg.creatorId == "Support")
			) {
				msg.creatorId = localizationService.t("CHAT.SUPPORT.NAME");
			} else {
				console.error(
					"Chat messages: CreatorId of message does not contain _. This is not a valid user id.",
					msg
				);
				msg.creatorId = "ERR_NAME";
			}
			msg.creatorIdMutated = true;
			return msg;
		});
	}

	@action.bound
	updateMessageList(messages: AmityMessageMutated[]) {
		this.messageList = messages as AmityMessageMutated[];
	}

	@action.bound
	updateSupportMessageList(messages: GroupedMessagesWithTotal) {
		this.supportChannelMessageList = messages;
	}

	@action.bound
	updateIconProps(iconProps: ChannelIconsDto) {
		this.iconProps = iconProps;
	}

	@action.bound
	setMessagePaginationObject(
		object: Amity.LiveCollection<Amity.Message<any>>,
		isSupport?: boolean
	) {
		if (!isSupport) {
			this.messagesPaginationObject = object;
			return;
		}
		this.supportMessagePaginationObject = object;
	}

	@action.bound
	setActiveChannel(channelInfo: Amity.Channel) {
		this.activeChannelInfo = channelInfo;
	}

	async checkIfMessageShouldBeFlagged(
		messageId: string,
		creatorId: string
	): Promise<void> {
		if (this.flaggingLoader.isLoadingProcess) {
			//if already waiting for a response, do not call request again to prevent user spam
			return;
		}

		this.flaggingLoader.suspend();

		try {
			const flaggingStatus =
				await ChatSDKService.isMessageFlaggedByMe(messageId);
			if (!flaggingStatus) {
				await ChatSDKService.flagMessage(messageId);
				return this.chatViewStore.rootStore.notificationStore.success(
					localizationService.t("CHAT.FLAG_MESSAGE", {
						"0": creatorId,
					})
				);
			}
			return this.chatViewStore.rootStore.notificationStore.error(
				localizationService.t("CHAT.ALREADY_FLAGGED_MESSAGE", {
					"0": creatorId,
				})
			);
		} catch (error) {
			console.error(error);
			this.chatViewStore.handleError(error);
			return;
		} finally {
			this.flaggingLoader.resume();
		}
	}

	async leaveCurrentChannel(): Promise<boolean> {
		if (!this.activeChannelInfo?.channelId) {
			console.error("Cannot leave channel, not in any channel");
			return false;
		}
		try {
			this.chatViewStore.chatListHandler.loaderStore.suspend();
			return await ChatSDKService.leaveChannel(
				this.activeChannelInfo.channelId
			);
		} catch (error) {
			console.error(error);
			this.chatViewStore.chatListHandler.loaderStore.resume();
			this.chatViewStore.handleError(error);
			return false;
		}
	}

	@action.bound
	updateDisposerList(disposer: Amity.Unsubscriber) {
		this.disposerList.push(disposer);
	}

	@action.bound
	disposeChannelListeners() {
		this.disposerList.map((fn) => fn());
		this.disposerList = [];
	}

	@action.bound
	disposeSupportChannel() {
		this.supportChatDisposer && this.supportChatDisposer();
		this.supportChannelId = null;
		this.supportMessagePaginationObject = null;
		this.supportChannelMessageList = null;
	}

	@action.bound
	disposeChannelHandler() {
		this.disposeChannelListeners();
		this.subscribedChannelId = null;
		this.activeChannelInfo = null;
		this.messagesPaginationObject = null;
		this.messageList = [];
		this.subscribedActiveSubChannelId = null;
		this.chatViewStore.setLeaveChannelDropdownOpened(false);
	}
}

export type AmityMessageMutated = Amity.Message<any> & {
	creatorIdMutated: boolean;
	imageUrl?: string;
};

interface GroupedMessages {
	[date: string]: AmityMessageMutated[];
}

interface GroupedMessagesWithTotal {
	totalMessages: number;
	groupedMessages: GroupedMessages;
	showAdditionalInfo: boolean;
}
